import { useEffect, useState } from 'react';
import AppWrappedSearchableAutocomplete from '../../AppWrappedSearchableAutocomplete';
import { fetchSchedulingRoleData } from '../_api';
import { EPIC_FIELDS, EPIC_FIELD_KEYS } from '../_const';

/**
 * User Credential input component for Epic/EpicCare stylized to work with Custom App Request Form
 * @param {Object} props properties
 * @param {boolean} props.isDisabled flag for disabled state
 * @returns Custom input
 */
const SchedulingRoleField = ({ isDisabled = false, ...props }) => {
	const FUNCTIONAL_ROLE_API_FAILURE_MESSAGE =
		'Unable to find any functional roles based on your input.  Please try again.';
	const [loadingStatus, setLoadingStatus] = useState(true);
	const [optionsList, setOptionsList] = useState([]);
	useEffect(() => {
		setLoadingStatus(true);
		fetchSchedulingRoleData(
			props.formik.values[EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT]
		)
			.then(response => {
				setOptionsList(response);
				setLoadingStatus(false);
			})
			.catch(e =>
				props.formik.setStatus({
					error: {
						source: props.name,
						cause: 'API Failure',
						details: e,
						message:
							'Unable to retrieve scheduling roles for the dropdown.  Please try again.',
					},
				})
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.formik.values[EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT]]);

	useEffect(() => {
		if (EPIC_FIELD_KEYS.TRAINING in props?.formik?.values) {
			if (props.formik.values[EPIC_FIELD_KEYS.TRAINING] !== 'Yes') {
				if (
					props.formik.values[props.name] !==
					EPIC_FIELDS[props.name].defaultValue
				) {
					props.formik.setFieldValue(
						props.name,
						EPIC_FIELDS[props.name].defaultValue
					);
				}
			} else {
				props.formik.setFieldValue(props.name, '');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.formik.values[EPIC_FIELD_KEYS.TRAINING]]);

	// If the default department changes, reset the value
	useEffect(() => {
		props.formik?.setFieldValue(props.name, '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.formik.values[EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT]]);

	/**
	 * Take user input and return a list of search results
	 * @param {string} value user input to search for
	 * @returns {string[]|object[]} list of results based on `value`
	 */
	const onSearchHandler = async value => {
		if (value) {
			let results = [...optionsList];
			if (value.length > 0) {
				results = optionsList.filter(o =>
					o.toLowerCase().includes(value.toLowerCase())
				);
			}
			return results.length > 200 ? results.slice(0, 200) : results;
		} else {
			return [];
		}
	};

	/**
	 * Process search results
	 * @param {string[]|object[]} resultsArr list of search results
	 * @returns {object[]} array of `key`, `value` pairs based on `resultsArr`
	 */
	const onSearchSuccess = resultsArr => {
		return resultsArr
			.map(key => ({
				key: key,
				value: key,
				category: 'Search Results',
			}))
			.concat(
				optionsList
					.filter(opt => !resultsArr.includes(opt))
					.map(key => ({
						key: key,
						value: key,
						category: 'Other Options',
					}))
			);
	};

	/**
	 * Gracefully handle errors during `onSearch` execution of `AppWrappedSearchableAutocomplete`
	 * @param {object} error error caught during `onSearch` workflow
	 * @returns {string[]} default list of results
	 */
	const onSearchFailure = error => {
		props.formik.setStatus({
			error: {
				source: props.name,
				cause: 'Input Failure',
				details: error,
				message: FUNCTIONAL_ROLE_API_FAILURE_MESSAGE,
			},
		});
		return [];
	};

	return (
		<AppWrappedSearchableAutocomplete
			label='Epic Scheduling Role'
			onSearchHandler={onSearchHandler}
			onSearchFailure={onSearchFailure}
			onSearchSuccess={onSearchSuccess}
			helperText={
				isDisabled ? (
					<>
						This field is disabled with the selection of{' '}
						<em>VIEW ONLY</em> because you indicated your training
						is not scheduled or completed.
					</>
				) : (
					<>
						Type to search for Epic Scheduling Roles.
						<br />
					</>
				)
			}
			isLoading={loadingStatus}
			isDisabled={isDisabled}
			{...props}
		/>
	);
};

export default SchedulingRoleField;
